<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import NumberInputWithComma from '@/corePagesModule/components/NumberInputWithComma.vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import TextButton from '@/components/input/Btn/TextButton.vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import NotificationBadge from '@/InventoryControlModule/components/NotificationBadge.vue';
import RecommendTag from '@/corePagesModule/components/RecommendTag.vue';
import InventoryAlertCell from '@/InventoryControlModule/components/InventoryTable/InventoryAlertCell.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import { ArrivalStatus } from '@/coreDomainsModule/entities/arrival';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IDate } from '@/coreDomainsModule/date';
import { OrderRepository } from '@/coreInfrastructureModule/Repositories/OrderRepository';
import { i18n } from '@/i18n';
import { diContainer } from '@/coreContainer/container';
import type { IParentBusiness } from '@/InventoryControlModule/domains/entities/Business';
import type { TableType } from '@/InventoryControlModule/composables/useTableState';
import type { IMonthlyBusinessData } from '@/InventoryControlModule/domains/entities/MonthlyData';
import { useTableCalculation } from '@/InventoryControlModule/composables/useTableCalculation';
import { storeToRefs } from 'pinia';
import { useInventoryStore } from '@/InventoryControlModule/pages/useInventoryStore';
import { InventoryTableViewModel } from '@/InventoryControlModule/pages/inventoryTableViewModel';
import { ForecastAccuracyService } from '@/InventoryControlModule/domains/services/forecastAccuracyService';
import { ACCURACY_THRESHOLDS } from '@/InventoryControlModule/constants/forecastAccuracy';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import SupplierForecastSaveButton from './SupplierForecastSaveButton.vue';

/**
 * OrderMonthlyColumn コンポーネントのプロパティ
 */
interface Props {
  /** ヘッダー情報 */
  header: {
    key: string;
    label: string;
    width: string;
    sticky: boolean;
    resizable?: boolean;
  };
  /** 対象の月 (相対月) */
  month: number;
  /** ビジネスアイテム */
  item: IParentBusiness;
  /** 仮想化アイテムタイプ */
  virtualItemType: 'parent' | 'child';
  /** 月次データ */
  monthlyData: Record<string, IMonthlyBusinessData>;
  /** 発注編集データ */
  orderEditData: any;
  /** 非表示のトランザクションヘッダーリスト */
  hiddenTransactionHeaderList: string[];
  /** 開いているビジネスID */
  isOpeningBusinessId: string;
  /** インベントリViewModel */
  inventoryVm: InventoryTableViewModel;
  /** 右端のボーダーを持つか(月別の最後のヘッダーか) */
  hasRightBorder: boolean;
  /** セルがハイライトされているか */
  isHighlighted: boolean;
  /** テーブル数値スタイル関数 */
  tableNumberStyleClass: (business: IParentBusiness, currentOpenId?: string, tableType?: TableType) => string;
  /** 負の値判定関数 */
  isNegative: (value?: number) => boolean;
  /** 近接推奨チェック関数 */
  checkNearestRecommend: (monthlyData: Record<string, IMonthlyBusinessData>, businessId: string, month: number) => boolean;
  /** 到着ステータス取得関数 */
  getArrivalStatusWhenDisplayButton: (monthlyData: Record<string, IMonthlyBusinessData>, businessId: string, month: number) => any;
  /** オーダーデータ確保関数 */
  ensureOrderData: (orderEditData: any, businessId: string, month: number) => void;
  /** 親ビジネス検索関数 */
  findParentBusinessById: (businessId: string) => IParentBusiness | undefined;
  /** 発注編集確定関数 */
  confirmOrderEdit: (orderRepository: OrderRepository, parentBusiness: IParentBusiness, month: number) => void;
  /** オーダー予末在庫計算関数 */
  calcOrderExceptedEnd: (
    monthlyData: Record<string, IMonthlyBusinessData>,
    orderEditData: any,
    item: IParentBusiness,
    month: number,
    forecastAccuracyData?: Record<string, any>
  ) => string;
  /** オーダー在庫回転率計算関数 */
  calcOrderTurnOver: (
    monthlyData: Record<string, IMonthlyBusinessData>,
    orderEditData: any,
    item: IParentBusiness,
    month: number,
    forecastAccuracyData?: Record<string, any>
  ) => string;
}

const props = defineProps<Props>();

const store = useInventoryStore();
const { state } = storeToRefs(store);

/**
 * OrderMonthlyColumn コンポーネントのイベント
 */
interface Emits {
  /** 発注数量更新イベント */
  updateAmount: [businessId: string, month: number, amount: number];
  /** 希望納期更新イベント */
  updateDesiredDate: [businessId: string, month: number, date: IDate];
  /** カレンダー入力フラグ更新イベント */
  updateCalendarFlag: [businessId: string, month: number, flag: boolean];
  /** 発注確定イベント */
  confirmOrder: [businessId: string, month: number];
}

const emit = defineEmits<Emits>();

// i18n関数
const t = i18n.t;

/**
 * 最適化されたテーブル計算Composable
 * WeakMapキャッシュによる月次データ計算の高速化
 */
const { getMonthlyData } = useTableCalculation();

/**
 * キャッシュされた月次データ
 */
const cachedMonthlyData = computed(() => {
  return getMonthlyData(props.monthlyData, props.item.businessId, props.month);
});

/**
 * 事前計算されたヘッダーキーの最後の部分
 */
const headerKeySuffix = computed(() => {
  return props.header.key.split('-').slice(-1)[0];
});

/**
 * 発注データのキャッシュ付きアクセス
 */
const orderData = computed(() => {
  props.ensureOrderData(props.orderEditData, props.item.businessId, props.month);
  return props.orderEditData[props.item.businessId][props.month];
});

// Recommendedが含まれるかどうか
const hasRecommended = computed(() => {
  const list = cachedMonthlyData.value?.arrivalStatusList;
  return Array.isArray(list) && list.includes(ArrivalStatus.Recommended);
});

/**
 * 親タイプの判定
 */
const isParent = computed(() => props.virtualItemType === 'parent');

/**
 * 計算済みスタイルクラス
 */
const tableNumberStyle = computed(() => {
  return props.tableNumberStyleClass(props.item, props.isOpeningBusinessId, 'virtualized');
});

/**
 * 内示の表示値を計算（設定内示正答率による補正対応）
 * @description 当月以降（month >= 0）で設定内示正答率が設定されている場合、before→afterの形式で表示
 * 親要素・子要素問わず適用される（各要素が個別の設定内示正答率を持つ）
 */
const displayAnnounce = computed(() => {
  const original = cachedMonthlyData.value.announce;
  if (!original) return '--';

  // 当月以降（month >= 0）の場合のみ補正値を表示
  if (props.month >= 0) {
    // 各取引の個別の設定内示正答率を使用
    const settingAccuracy = state.value.forecastAccuracyData[props.item.businessId]?.settingAccuracy;

    if (settingAccuracy && settingAccuracy !== 100) {
      const adjusted = ForecastAccuracyService.calculateAdjustedForecast(original, settingAccuracy);
      return `${original.roundToDecimal(2).addComma()}→${adjusted.roundToDecimal(2).addComma()}`;
    }
  }

  return original.roundToDecimal(2).addComma();
});

// 仕入先内示の入力値をセル単位でキャッシュ
const supplierForecastInputCache = reactive<Record<string, number>>({});

/** 仕入先内示セルのUI状態 */
type SupplierForecastUiStatus = 'idle' | 'loading' | 'success' | 'error';

const supplierForecastUiStatusMap = reactive<Record<string, SupplierForecastUiStatus>>({});

const isSupplierForecastErrorModalOpen = ref(false);

/**
 * セル識別用のキャッシュキーを組み立てる
 * @param businessId - ビジネスID
 * @param month - 月インデックス
 * @returns キャッシュキー
 */
const getSupplierForecastKey = (businessId: string, month: number) => `${businessId}-${month}`;

/**
 * キャッシュされた仕入先内示の入力値を取得
 * @param businessId - ビジネスID
 * @param month - 月インデックス
 * @param fallback - キャッシュ未設定時の初期値
 * @returns キャッシュ済みまたは初期値
 */
const getSupplierForecastInput = (businessId: string, month: number, fallback: number) => {
  const key = getSupplierForecastKey(businessId, month);
  if (!(key in supplierForecastInputCache)) supplierForecastInputCache[key] = fallback;
  return supplierForecastInputCache[key] ?? fallback;
};

/**
 * 仕入先内示の入力値をキャッシュに保存
 * @param businessId - ビジネスID
 * @param month - 月インデックス
 * @param val - 保存する値
 */
const setSupplierForecastInput = (businessId: string, month: number, val: number) => {
  supplierForecastInputCache[getSupplierForecastKey(businessId, month)] = val;
};

/**
 * 仕入先内示の入力値が保存済みと異なるか判定する
 * @param businessId - ビジネスID
 * @param month - 月インデックス
 * @param origin - 保存済みの値
 * @returns 値に変更があればtrue
 */
const hasSupplierForecastChanged = (businessId: string, month: number, origin: number): boolean => {
  const current = getSupplierForecastInput(businessId, month, origin);
  return !Object.is(current, origin);
};

/**
 * UI状態が未初期化なら生成して返す
 * @param key - セルを一意に識別するキー
 */
/**
 * セルごとのUI状態を更新する
 * @param key - セルを一意に識別するキー
 * @param status - 設定するUI状態
 */
const setSupplierForecastUiStatusByKey = (key: string, status: SupplierForecastUiStatus) => {
  supplierForecastUiStatusMap[key] = status;
};

/**
 * セルごとのUI状態を取得する
 * @param businessId - ビジネスID
 * @param month - 対象月
 */
const getSupplierForecastUiStatus = (businessId: string, month: number): SupplierForecastUiStatus => {
  const key = getSupplierForecastKey(businessId, month);
  return supplierForecastUiStatusMap[key] ?? 'idle';
};

/**
 * 編集を無効化すべきか判定する
 * @param businessId - ビジネスID
 * @param month - 対象月
 */
const isSupplierForecastDisabled = (businessId: string, month: number): boolean => {
  const status = getSupplierForecastUiStatus(businessId, month);
  return status === 'loading' || status === 'success';
};

/**
 * コンポーネントから通知されたUI状態を保存する
 * @param businessId - ビジネスID
 * @param month - 月インデックス
 * @param status - 新しいUI状態
 */
const handleSupplierForecastStatusChange = (businessId: string, month: number, status: SupplierForecastUiStatus) => {
  setSupplierForecastUiStatusByKey(getSupplierForecastKey(businessId, month), status);
};

/**
 * 保存処理で発生した例外をログ出力する
 * @param error - 発生した例外
 */
const handleSupplierForecastError = async (error: unknown) => {
  console.error(error);
  if (isSupplierForecastErrorModalOpen.value) return;

  isSupplierForecastErrorModalOpen.value = true;
  try {
    await showInfoModal({
      modalTitle: t('エラーが発生しました'),
      modalDescription: t('仕入れ先内示の保存に失敗しました。時間をおいて再度お試しください。'),
      backButtonLabel: t('閉じる'),
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
  } finally {
    isSupplierForecastErrorModalOpen.value = false;
  }
};

/**
 * 仕入先内示の値を保存して状態を更新
 * @param businessId - ビジネスID
 * @param month - 月インデックス
 * @param origin - 元の値
 */
const saveSupplierForecast = async (businessId: string, month: number, origin: number) => {
  const amount = getSupplierForecastInput(businessId, month, origin);
  const result = await diContainer.upsertTransactionValuesUseCase.Execute(businessId, [
    {
      type: 'supplierForecast',
      month,
      value: amount,
      originValue: origin,
    },
  ]);
  if (result.status.type === 'success') {
    setSupplierForecastInput(businessId, month, amount);
    await store.refreshBusiness(businessId);
    return;
  }
  if (result.status.type === 'error') {
    throw result.status.error;
  }
  throw new Error('failed to save supplier forecast');
};

/**
 * 数値表示において、undefined/NaN/(0)かそうでないかを判定して表示内容を返す関数
 * @param value - 判定する数値
 * @param needsExceptZero - 0を除外するかどうか
 */
const getAmountDisplayContent = (value: number | undefined, needsExceptZero = false) => {
  return value === undefined || Number.isNaN(value) || (needsExceptZero && value === 0) ? '--' : value.roundToDecimal(2).addComma();
};

/**
 * 前月内示正答率の表示値を取得
 * @param item - ビジネスアイテム
 * @returns 表示文字列
 */
const getPreviousMonthAccuracyDisplay = (item: IParentBusiness): string => {
  // 前月（-1月）のデータを取得
  const previousMonthData = getMonthlyData(state.value.monthlyData, item.businessId, -1);
  if (!previousMonthData) return '--';

  const accuracy = ForecastAccuracyService.calculatePreviousMonthAccuracy(
    previousMonthData.announce || 0,
    previousMonthData.salesResult || 0
  );

  return accuracy !== undefined ? `${getAmountDisplayContent(accuracy)}%` : '--';
};

/**
 * 前月内示正答率の色分けクラスを取得
 * @param item - ビジネスアイテム
 * @returns CSSクラス名
 */
const getPreviousMonthAccuracyClass = (item: IParentBusiness): string => {
  const previousMonthData = getMonthlyData(state.value.monthlyData, item.businessId, -1);
  if (!previousMonthData) return '';

  const accuracy = ForecastAccuracyService.calculatePreviousMonthAccuracy(
    previousMonthData.announce || 0,
    previousMonthData.salesResult || 0
  );

  if (!accuracy) return '';

  if (accuracy <= ACCURACY_THRESHOLDS.CRITICAL) {
    return 'forecast-accuracy--critical';
  } else if (accuracy > ACCURACY_THRESHOLDS.CRITICAL && accuracy <= ACCURACY_THRESHOLDS.WARNING) {
    return 'forecast-accuracy--warning';
  }
  return '';
};
</script>

<template>
  <EzCell
    is-number
    :is-highlighted="isHighlighted"
    :is-negative="isNegative(cachedMonthlyData[headerKeySuffix])"
    :class="tableNumberStyle"
    :has-right-border="hasRightBorder"
  >
    <template v-if="!hiddenTransactionHeaderList.includes(header.key)">
      <!-- 入荷済 -->
      <template v-if="header.key.includes('-alreadyArrival') && isParent">
        {{ getAmountDisplayContent(cachedMonthlyData.deliveredArrival, true) }}
      </template>

      <!-- 予入荷: ArrivalStatusがRecommendedのときは、推奨ボタンとBadgeのみ（数値を表示しない） -->
      <template v-else-if="header.key.includes('-expectedArrival') && isParent">
        <div class="inventory-badge__container" :class="tableNumberStyle">
          <template v-if="hasRecommended">
            <router-link :to="{ name: 'recommend-order', params: { id: item.businessId } }" target="_blank">
              <RecommendTag :status="ArrivalStatus.Recommended" />
            </router-link>
            <div class="inventory-badge__list">
              <template v-for="arrivalStatus in cachedMonthlyData.arrivalStatusList" :key="`${item.businessId}-${arrivalStatus}`">
                <NotificationBadge
                  v-if="arrivalStatus !== ArrivalStatus.Recommended && arrivalStatus !== ArrivalStatus.Nothing"
                  class="inventory-badge"
                  :style="inventoryVm.notificationBadgeStyle(arrivalStatus, true)"
                  :arrival-status="arrivalStatus"
                  :unit="item.unit"
                  :un-paid-arrivals="cachedMonthlyData.unPaidArrival"
                  :approval-requested-arrival="cachedMonthlyData.approvalRequestedArrival"
                />
              </template>
            </div>
          </template>
          <template v-else>
            <div v-if="(cachedMonthlyData.exceptedArrivalForOrderMode ?? 0) > 0" class="inventory-badge__list">
              <template v-for="arrivalStatus in cachedMonthlyData.arrivalStatusList" :key="`${item.businessId}-${arrivalStatus}`">
                <NotificationBadge
                  v-if="arrivalStatus !== ArrivalStatus.Nothing"
                  class="inventory-badge"
                  :style="inventoryVm.notificationBadgeStyle(arrivalStatus, true)"
                  :arrival-status="arrivalStatus"
                  :unit="item.unit"
                  :un-paid-arrivals="cachedMonthlyData.unPaidArrival"
                  :approval-requested-arrival="cachedMonthlyData.approvalRequestedArrival"
                />
              </template>
            </div>
            {{ getAmountDisplayContent(cachedMonthlyData.exceptedArrivalForOrderMode, true) }}
          </template>
        </div>
      </template>

      <!-- 前月内示正答率 -->
      <template v-else-if="month === 0 && header.key.includes('-previousMonthAccuracy')">
        <div :class="getPreviousMonthAccuracyClass(item)">{{ getPreviousMonthAccuracyDisplay(item) }}</div>
      </template>

      <!-- 設定内示正答率(親取引のみ) -->
      <template v-else-if="month === 0 && header.key.includes('-settingAccuracy')">
        <template v-if="virtualItemType === 'parent'">
          <div class="forecast-accuracy-setting">
            <!-- 親取引・子取引共に編集可能な入力フィールド -->
            <NumberInputWithComma
              :model-value="state.forecastAccuracyData[item.businessId]?.settingAccuracy || 100"
              class="table-input"
              suffix="%"
              @input="(val) => store.updateForecastAccuracySettingAccuracy(item.businessId, parseFloat(val))"
            />
          </div>
        </template>
      </template>

      <!-- 内示正答率履歴（親取引のみ） -->
      <template v-else-if="month === 0 && header.key.includes('-accuracyHistory')">
        <template v-if="virtualItemType === 'parent'">
          <div class="forecast-accuracy-history-container">
            <button class="forecast-accuracy-history-btn" @click="() => store.openForecastAccuracyHistoryModal(item.businessId)">
              <SvgLoader name="history" />
            </button>
          </div>
        </template>
      </template>

      <!-- 発注数量入力: orderEditDataの初期化ガード付き -->
      <template v-else-if="header.key.includes('-amount') && virtualItemType === 'parent'">
        <NumberInputWithComma
          :model-value="(ensureOrderData(orderEditData, item.businessId, month), orderEditData[item.businessId][month].amount)"
          class="table-input"
          :class="`table-input--${orderEditData[item.businessId][month].status}`"
          @input="
            (val) => {
              ensureOrderData(orderEditData, item.businessId, month);
              emit('updateAmount', item.businessId, month, parseFloat(val));
            }
          "
        />
      </template>

      <!-- 希望納期カレンダー: orderEditDataの初期化ガード付き -->
      <template v-else-if="header.key.includes('-desiredDeliveryDate') && virtualItemType === 'parent'">
        <div class="table-cell table-calendar__container">
          <template
            v-if="orderEditData[item.businessId][month].isEmptyCalendarInputClickedOnce || orderEditData[item.businessId][month].status"
          >
            <VueDatePicker
              :model-value="DateService.convertIDateToDate(orderEditData[item.businessId][month].crossDate as IDate)"
              :class="`table-input table-input--${orderEditData[item.businessId][month].status}`"
              :mode-height="1"
              format="MM/dd"
              :enable-time-picker="false"
              :month-change-on-scroll="false"
              :teleport="true"
              week-start="0"
              :locale="i18n.locale"
              :placeholder="t('日付を選択')"
              :select-text="t('選択')"
              :cancel-text="t('キャンセル')"
              :show-now-button="true"
              :clearable="false"
              :now-button-label="t('現在を選択')"
              pad
              @update:model-value="emit('updateDesiredDate', item.businessId, month, DateService.convertDateToIDate($event))"
            />
          </template>
          <button v-else class="table-calendar__default" @click="emit('updateCalendarFlag', item.businessId, month, true)">
            <Mdi class="table-calendar__default__icon" icon="calendar_today" />
          </button>
        </div>
      </template>

      <!-- 発注確定ボタン: orderEditDataの初期化ガード付き -->
      <template v-else-if="header.key.includes('-createOrder') && virtualItemType === 'parent'">
        <TextButton
          class="table-button table-button--confirm"
          button-style="btn-primary"
          :on-click="
            () => {
              ensureOrderData(orderEditData, item.businessId, month);
              emit('confirmOrder', item.businessId, month);
            }
          "
        >
          {{ t('確定') }}
        </TextButton>
      </template>

      <!-- 予末在庫 -->
      <template v-else-if="header.key.includes('-expectedEndInventory') && virtualItemType === 'parent'">
        <!-- 発注の入力値と内示正答率に応じて表示を変える -->
        {{ calcOrderExceptedEnd(monthlyData, orderEditData, item, month, state.forecastAccuracyData) }}
      </template>

      <!-- 在庫回転率(Turn Over) -->
      <template v-else-if="header.key.includes('-turnOver') && virtualItemType === 'parent'">
        <!-- 発注の入力値と内示正答率に応じて表示を変える -->
        {{ calcOrderTurnOver(monthlyData, orderEditData, item, month, state.forecastAccuracyData) }}
      </template>

      <!-- アラート -->
      <template v-else-if="header.key.includes('-alert') && virtualItemType === 'parent'">
        <InventoryAlertCell :prop-business="item" :prop-relative-month="month" />
      </template>

      <!-- 通常数値表示 -->
      <template v-else>
        <!-- 親要素もしくは子要素で内示と販売実績の場合はデータを表示 -->
        <template
          v-if="
            virtualItemType === 'parent' ||
            header.key.includes('-announce') ||
            header.key.includes('-salesResult') ||
            header.key.includes('-supplierAnnounce')
          "
        >
          <!-- 内示の場合は補正機能付きの表示を使用（親・子共通） -->
          <template v-if="header.key.includes('-announce')">
            {{ displayAnnounce }}
          </template>
          <!-- 仕入先内示は当月以降、親行で入力+保存 -->
          <template v-else-if="header.key.includes('-supplierAnnounce')">
            <template v-if="virtualItemType === 'parent' && month >= 0">
              <div class="supplier-forecast__controls">
                <NumberInputWithComma
                  :model-value="getSupplierForecastInput(item.businessId, month, cachedMonthlyData.supplierAnnounce || 0)"
                  class="table-input"
                  :disabled="isSupplierForecastDisabled(item.businessId, month)"
                  @input="(val) => setSupplierForecastInput(item.businessId, month, parseFloat(val))"
                />
                <SupplierForecastSaveButton
                  :save-handler="() => saveSupplierForecast(item.businessId, month, cachedMonthlyData.supplierAnnounce || 0)"
                  :disabled="isSupplierForecastDisabled(item.businessId, month)"
                  :can-save="hasSupplierForecastChanged(item.businessId, month, cachedMonthlyData.supplierAnnounce || 0)"
                  :aria-label="t('保存')"
                  :loading-aria-label="t('保存中')"
                  :success-aria-label="t('保存完了')"
                  :error-aria-label="t('保存に失敗しました。再度保存するには選択してください')"
                  @status-change="(status) => handleSupplierForecastStatusChange(item.businessId, month, status)"
                  @error="handleSupplierForecastError"
                />
              </div>
            </template>
            <template v-else-if="virtualItemType !== 'parent'">--</template>
            <template v-else>
              {{ getAmountDisplayContent(cachedMonthlyData[header.key.split('-').slice(-1)[0]]) }}
            </template>
          </template>
          <!-- 内示以外は通常の表示 -->
          <template v-else>
            {{ getAmountDisplayContent(cachedMonthlyData[header.key.split('-').slice(-1)[0]]) }}
          </template>
        </template>
        <!-- 子要素のうち発注以外の部分は -- を表示 -->
        <template
          v-else-if="
            !header.key.includes('-amount') && !header.key.includes('-desiredDeliveryDate') && !header.key.includes('-createOrder')
          "
        >
          --
        </template>
      </template>
    </template>
  </EzCell>
</template>

<style lang="scss" scoped>
@import '@/InventoryControlModule/pages/scss/inventory-table.scss';
@import '@/assets/scss/baseLayout/color.scss';

/* 内示正答率関連のスタイル */
.forecast-accuracy {
  &-cell {
    text-align: center;
    padding: 4px 8px;
  }

  &--critical {
    background-color: $semanticErrorSubdued;
    color: $semanticError;
  }

  &--warning {
    color: $semanticWarning;
  }

  &-setting {
    display: grid;
    place-items: center;
    width: 100%;
  }

  &-history {
    &-container {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
    }

    &-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      color: var(--color-primary);
      transition: all 0.2s ease;

      &:hover {
        background: var(--color-primary-subdued);
        color: var(--color-primary-dark);
      }

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }
}

.supplier-forecast__controls {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
}

.table-cell {
  &__content {
    &--detail {
      justify-content: center;

      :deep(.table__cell__content) {
        padding: 0 8px;
      }
    }
  }
}
</style>
